import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaRocket, FaCog, FaChartLine, FaCheckCircle } from 'react-icons/fa';

const HowItWorksSection = styled.section`
  padding: 5rem 2rem;
  background: #f8f9fa;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
`;

const StepsContainer = styled.div`
  position: relative;
  margin-bottom: 4rem;
`;

const StepLine = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #0891b2, transparent);
  z-index: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Step = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin: 0 auto 1.5rem;
  position: relative;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: -10px;
    background: radial-gradient(circle, rgba(8, 145, 178, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    z-index: -1;
  }
`;

const StepIcon = styled.div`
  font-size: 1.5rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
`;

const StepDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  font-weight: 400;
`;

const CTASection = styled(motion.div)`
  text-align: center;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0891b2;
  border-radius: 30px;
  padding: 4rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(8, 145, 178, 0.1);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
`;

const CTATitle = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 800;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.2rem;
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  color: white;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(8, 145, 178, 0.2);
  
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(8, 145, 178, 0.3);
  }
`;

const steps = [
  {
    icon: <FaRocket />,
    title: "Quick Setup",
    description: "Connect Faroflow to your casino platform in minutes with our simple API integration"
  },
  {
    icon: <FaCog />,
    title: "Customize AI",
    description: "Train the AI with your casino's specific games, bonuses, and policies"
  },
  {
    icon: <FaChartLine />,
    title: "Monitor & Optimize",
    description: "Track performance metrics and continuously improve player interactions"
  },
  {
    icon: <FaCheckCircle />,
    title: "Scale Success",
    description: "Expand coverage and capabilities as your player base grows"
  }
];

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <HowItWorksSection id="how-it-works">
      <Container ref={ref}>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Get Started in Days, Not Months
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Our streamlined implementation process gets you up and running quickly
          </SectionSubtitle>
        </SectionHeader>

        <StepsContainer>
          <StepLine />
          <StepsGrid>
            {steps.map((step, index) => (
              <Step
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <StepNumber>
                  <StepIcon>{step.icon}</StepIcon>
                </StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Step>
            ))}
          </StepsGrid>
        </StepsContainer>

        <CTASection
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <CTATitle>Ready to Transform Your Player Support?</CTATitle>
          <CTASubtitle>
            Join leading online casinos already using Faroflow to deliver exceptional player experiences
          </CTASubtitle>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            Get Started
          </CTAButton>
        </CTASection>
      </Container>
    </HowItWorksSection>
  );
};

export default HowItWorks; 